/* eslint-disable react-hooks/exhaustive-deps */
import {
  FormControl,
  InputLabel,
  Stack,
  TextField,
  IconButton,
  useTheme,
  Button,
  useMediaQuery,
  Typography,
  Tooltip,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import { useLocation } from 'react-router';
import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
  initialState,
  reducerUpdateAdvanceClear,
  reducerUpdateAdvanceSearch,
  reducerUpdateAdvanceSearchIncludeKeywords
} from 'src/features/advanceSearchSocialMedia';
import AdvanceSearchSocialMedia from 'src/components/AdvanceSearchSocialMedia';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import changeObjectToQuery from 'src/utils/changeObjectToQuery';
import TooltipCustomize from 'src/components/TooltipCustomize';
import useComparison from 'src/services/useComparison';
import {
  IRawPostmadeOutputEntity,
  ITablePaginationAttribute
} from 'src/models/general';
import { fillParamAdvanceSearchEmpty } from 'src/utils/fillParamAdvanceSearchEmpth';
import RefreshIcon from '@mui/icons-material/Refresh';
import useQueryUrl from 'src/hooks/useQuery';
import { useGranularity } from 'src/hooks/useGranularity';
import SelectAutoRefreshInterval from 'src/components/SelectAutoRefreshInterval';
import DateRangeLabel from 'src/layouts/SidebarLayout/components/DateRangeLabel';
import SelectDateRangeRetail from 'src/components/Retail/SelectDateRangeRetail';
import { RestoreFromTrash } from '@mui/icons-material';
import { reducerUpdateComparisonDateRange } from 'src/features/sosmedComparison';

const ChipCustom = styled(Chip)(
  ({ theme }) => `
    background-color: ${theme.palette.primary.light};
    border-radius: ${theme.spacing(1)};
    padding: 10px ${theme.spacing(2)};
    height: 29px;
    width: 100px;
    .MuiChip-label {
        color: #0A789D;
        font-size: 14px;
        line-height: 130%;
        margin: 0 4px;
    }
    .MuiChip-deleteIcon {
        font-size: 14px;
        color: #0A789D;
    }
  `
);

function BottomHeader() {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const { pathname } = useLocation();
  const title = pathname.split('/')[2];
  const queryURL = useQueryUrl();
  const comparisonId = queryURL.get('id');
  const { granular, setGlanular, getGranularityUnit } = useGranularity();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const advanceSearch = useAppSelector(
    (state) => state.storeAdvanceSearchSocialMedia
  );
  const {
    streamsCommentingToPost,
    selectedOriginalToPost,
    searchOriginalToPost,
    ...rest
  } = advanceSearch;

  const query = changeObjectToQuery(rest);
  const [localSearch, setLocalSearch] = useState<string>('');

  const advanceSearchKeys = query
    ? query.split(';').slice(0, query.split(';').length - 1)
    : [];

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') handleSearch();
  };

  const handleClear = () => {
    setLocalSearch('');
    dispatch(reducerUpdateAdvanceClear(initialState));
  };

  const handleSearch = () => {
    dispatch(reducerUpdateAdvanceSearchIncludeKeywords([localSearch]));
    setLocalSearch('');
  };

  const handleDeleteFilter = (item: string) => {
    const splitFilter = item.split(':');
    const deletedFilter = splitFilter[0].trim();
    dispatch(
      reducerUpdateAdvanceSearch({
        ...advanceSearch,
        [deletedFilter]: initialState[deletedFilter]
      })
    );
  };

  const handleDeleteAllFilter = () => {
    dispatch(reducerUpdateAdvanceSearch(initialState));
  };

  const [open, setOpen] = useState<boolean>(false);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const projectId = useAppSelector((state) => state.users.project.id);

  const advanceSearchSocialMedia = useAppSelector(
    (state) => state.storeAdvanceSearchSocialMedia
  );

  const fillAdvanceSearchSocialMedia = useMemo(() => {
    return fillParamAdvanceSearchEmpty(advanceSearchSocialMedia);
  }, [advanceSearchSocialMedia]);

  const { dateRangeFormatted } = useAppSelector(
    (state) => state.analyticArguments
  );
  const comparisonDateRange = useAppSelector(
    (state) => state.storeComparison.dateRange
  );
  const comparisonDateRangeFormatted = useAppSelector(
    (state) => state.storeComparison.dateRangeFormatted
  );
  const isComparation = useAppSelector(
    (state) => state.storeComparison.isComparation
  );

  const initialTableAttribute: ITablePaginationAttribute<IRawPostmadeOutputEntity> =
    {
      columnName: 'engagementRate',
      limit: 20,
      page: 1,
      sortingMethod: 'desc'
    };

  const timezone = useAppSelector((state) => state.users.project.timezone);

  const finalPayload = {
    ...fillAdvanceSearchSocialMedia,
    projectId,
    ...dateRangeFormatted,
    objectList: [],
    granularity: getGranularityUnit({ ...dateRangeFormatted }),
    timezone,
    ...initialTableAttribute,
    comparisonId
  };

  const {
    getAllMetricCompatisonSosmed,
    getDayToDayEngagement,
    getDayToDayFollowersGrowth,
    getDayToDayTalk
  } = useComparison();

  const { engagementGranularity, followersGranularity, talkGranularity } =
    useAppSelector((state) => state.storeComparison);

  const handleRefresh = () => {
    dispatch(reducerUpdateComparisonDateRange(comparisonDateRange));
    getDayToDayEngagement({
      ...finalPayload,
      granularity: engagementGranularity
    });
    getDayToDayFollowersGrowth({
      ...finalPayload,
      granularity: followersGranularity
    });
    getDayToDayTalk({
      ...finalPayload,
      granularity: talkGranularity
    });
    getAllMetricCompatisonSosmed(finalPayload);
  };

  // auto refresh metric
  const { selectedAutoRefreshInterval } = useAppSelector(
    (store) => store.autoRefreshMetric
  );
  useEffect(() => {
    if (selectedAutoRefreshInterval === 0) return;
    const interval = setInterval(() => {
      handleRefresh();
    }, selectedAutoRefreshInterval);

    return () => clearInterval(interval);
  }, [selectedAutoRefreshInterval, comparisonDateRange]);

  return (
    <Stack direction="column" my={2}>
      <Stack
        direction={isLargeScreen ? 'row' : 'column'}
        justifyContent={isLargeScreen ? 'space-between' : 'unset'}
        gap={isLargeScreen ? 3 : 2}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={matchesUpMd ? 1 : 0}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            className="filtering"
            width="100%"
            maxWidth={isLargeScreen ? '400px' : 'unset'}
          >
            <TextField
              value={localSearch}
              size="small"
              InputProps={{
                startAdornment: (
                  <IconButton size="small" onClick={handleSearch}>
                    <SearchTwoToneIcon />
                  </IconButton>
                ),
                endAdornment: localSearch ? (
                  <IconButton size="small" onClick={handleClear}>
                    <ClearIcon />
                  </IconButton>
                ) : null,
                sx: {
                  borderRadius: '16px',
                  width: '100%',
                  backgroundColor: 'white'
                }
              }}
              placeholder="Search stream"
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              fullWidth
            />
            <TooltipCustomize title="Advance search" placement="top-start">
              <Button
                variant="contained"
                sx={{
                  borderRadius: '16px',
                  height: theme.spacing(5),
                  minWidth: theme.spacing(6),
                  background: theme.palette.primary.main,
                  display: !matchesUpMd && 'none'
                }}
                onClick={() => handleClickOpen()}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ px: 1 }}
                >
                  <FilterListIcon htmlColor="#FFFFFF" />
                  <Typography color="white" fontWeight={700}>
                    Filter
                  </Typography>
                </Stack>
              </Button>
            </TooltipCustomize>
            {open && (
              <AdvanceSearchSocialMedia open={open} onClose={handleClose} />
            )}
          </Stack>
          <Stack
            direction="row"
            overflow="auto"
            maxWidth={500}
            spacing={1}
            sx={{
              scrollbarWidth: 'thin'
            }}
          >
            {matchesUpMd && advanceSearchKeys
              ? advanceSearchKeys.map((item) => {
                  return (
                    <Tooltip title={item}>
                      <ChipCustom
                        variant="filled"
                        deleteIcon={<ClearIcon />}
                        onDelete={() => handleDeleteFilter(item)}
                        label={item}
                        size="small"
                      />
                    </Tooltip>
                  );
                })
              : null}
          </Stack>
          {matchesUpMd && advanceSearchKeys.length > 0 ? (
            <Stack
              direction="row"
              alignItems="center"
              sx={{ cursor: 'pointer' }}
              onClick={handleDeleteAllFilter}
            >
              <RestoreFromTrash fontSize="small" />
              <Typography fontSize="14px">Clear Filters</Typography>
            </Stack>
          ) : null}
        </Stack>
        <TooltipCustomize title="Advance search" placement="top-start">
          <Button
            variant="contained"
            sx={{
              borderRadius: '16px',
              height: theme.spacing(5),
              minWidth: theme.spacing(6),
              background: theme.palette.primary.main,
              display: matchesUpMd && 'none'
            }}
            onClick={() => handleClickOpen()}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ px: 1 }}
            >
              <FilterListIcon htmlColor="#FFFFFF" />
              <Typography color="white" fontWeight={700}>
                Filter
              </Typography>
            </Stack>
          </Button>
        </TooltipCustomize>
        <Stack direction="row" display={matchesUpMd && 'none'}>
          <Stack
            direction="row"
            overflow="auto"
            maxWidth={500}
            spacing={1}
            sx={{
              scrollbarWidth: 'thin'
            }}
          >
            {advanceSearchKeys
              ? advanceSearchKeys.map((item) => {
                  return (
                    <Tooltip title={item}>
                      <ChipCustom
                        variant="filled"
                        deleteIcon={<ClearIcon />}
                        onDelete={() => handleDeleteFilter(item)}
                        label={item}
                        size="small"
                      />
                    </Tooltip>
                  );
                })
              : null}
          </Stack>
          {advanceSearchKeys.length > 0 ? (
            <Stack
              direction="row"
              alignItems="center"
              sx={{ cursor: 'pointer' }}
              onClick={handleDeleteAllFilter}
            >
              <RestoreFromTrash fontSize="small" />
              <Typography fontSize="14px">Clear Filters</Typography>
            </Stack>
          ) : null}
        </Stack>
        <Stack
          direction={matchesUpMd ? 'row' : 'column'}
          justifyContent={isLargeScreen ? 'unset' : 'space-between'}
          spacing={2}
          className="timeframeAndRefresh"
        >
          <Stack direction={'row'} spacing={1} alignItems={'center'}>
            <FormControl
              size="small"
              sx={{ width: matchesUpMd ? 135 : '100%' }}
            >
              {isComparation ? <InputLabel id="date-range-label">Period 1</InputLabel> : null}
              <SelectDateRangeRetail
                selectProps={{
                  ...(isComparation ? { labelId: 'date-range-label', label: 'Period 1' } : {}),
                  sx: {
                    borderRadius: '16px'
                  }
                }}
              />
            </FormControl>
            {isComparation ? (
              <>
                <Typography variant="caption" fontWeight={600}>
                  vs
                </Typography>
                <FormControl
                  size="small"
                  sx={{ width: matchesUpMd ? 135 : '100%' }}
                >
                  <InputLabel id="comparison-date-range-label">Period 2</InputLabel>
                  <SelectDateRangeRetail
                    comparison
                    selectProps={{
                      labelId: 'comparison-date-range-label',
                      label: 'Period 2',
                      sx: {
                        borderRadius: '16px'
                      }
                    }}
                  />
                </FormControl>
              </>
            ) : null}
          </Stack>
          <Stack gap={1} direction="row">
            <TooltipCustomize title="Refresh all metric" placement="top-start">
              <Button
                variant="contained"
                onClick={handleRefresh}
                sx={{
                  borderRadius: '6px',
                  height: theme.spacing(5),
                  minWidth: theme.spacing(6),
                  background: theme.palette.primary.main
                }}
              >
                <RefreshIcon />
              </Button>
            </TooltipCustomize>
            <FormControl size="small" sx={{ width: 100 }}>
              <SelectAutoRefreshInterval />
            </FormControl>
          </Stack>
        </Stack>
      </Stack>

      <DateRangeLabel
        label={`Viewing ${title} within`}
        secondDateRange={
          isComparation ? comparisonDateRangeFormatted : undefined
        }
      />
    </Stack>
  );
}

export default BottomHeader;
